import React, { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { adminAPI } from '@/lib/adminApi';
import { AddClientDialog } from '@/components/admin/AddClientDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Search, Loader2, AlertCircle, ArrowRight,
  Users, ChevronLeft, ChevronRight, UserPlus
} from 'lucide-react';

export default function AdminUsers() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [addOpen, setAddOpenState] = useState(searchParams.get('add') === '1');
  const setAddOpen = (open) => {
    setAddOpenState(open);
    if (!open && searchParams.has('add')) setSearchParams({}, { replace: true });
  };
  
  const pageSize = 20;
  const totalPages = Math.ceil(total / pageSize);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin, page, search]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminAPI.listUsers(page, pageSize, search);
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  // Redirect non-admins
  if (!isAdmin) {
    return <Navigate to="/orders" replace />;
  }

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Button 
            asChild
            variant="ghost" 
            className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2"
          >
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </Button>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-12 h-12 bg-[#C6A14A]/20 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6 text-[#C6A14A]" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-light text-white tracking-wide">Clients</h1>
              <p className="text-[#E8E4DE]/60 mt-1">{total} accounts</p>
            </div>
            <Button onClick={() => setAddOpen(true)} className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]" data-testid="add-client-btn">
              <UserPlus className="mr-2 h-4 w-4" /> Add Client
            </Button>
          </div>
          <AddClientDialog open={addOpen} onOpenChange={setAddOpen} />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#E8E4DE]/40" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name, email, phone or brokerage..."
                data-testid="client-search-input"
                className="pl-10 bg-[#0f1724] border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <Button type="submit" className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]" data-testid="client-search-btn">
              Search
            </Button>
          </div>
        </form>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-3 mb-6 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Users List */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 text-[#C6A14A] animate-spin" />
              </div>
            ) : users.length === 0 ? (
              <p className="text-center text-[#E8E4DE]/60 py-12">No users found</p>
            ) : (
              <div className="divide-y divide-white/5">
                {users.map((u) => (
                  <Link 
                    key={u.id}
                    to={`/admin/clients/${u.id}`}
                    data-testid={`client-row-${u.id}`}
                    className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#C6A14A]/10 rounded-full flex items-center justify-center">
                        <span className="text-[#C6A14A] font-medium">
                          {(u.full_name || u.email).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-white group-hover:text-[#C6A14A] transition-colors">
                          {u.full_name || u.email}
                        </p>
                        {u.full_name && (
                          <p className="text-sm text-[#E8E4DE]/60">
                            {u.email}{u.company ? ` · ${u.company}` : ''}{u.phone ? ` · ${u.phone}` : ''}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-[#E8E4DE]/40">
                            Joined {new Date(u.created_at).toLocaleDateString()}
                          </span>
                          {u.email_verified && (
                            <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                              Verified
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {u.roles?.includes('admin') && (
                        <Badge className="bg-[#C6A14A]/20 text-[#C6A14A] border-[#C6A14A]/30">
                          Admin
                        </Badge>
                      )}
                      <ArrowRight className="h-4 w-4 text-[#C6A14A] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-[#E8E4DE]/60">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-white/10 text-[#E8E4DE]"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-white/10 text-[#E8E4DE]"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
